'use client';
import { useQueryClient } from '@tanstack/react-query';
import { authenticateUserInRestAPI } from '@vimond/services/rest-api';
import { Amplify } from 'aws-amplify';
import {
  autoSignIn as ampAutoSignIn,
  confirmResetPassword as ampConfirmResetPassword,
  confirmSignIn as ampConfirmSignIn,
  confirmSignUp as ampConfirmSignup,
  deleteUser as ampDeleteUser,
  resendSignUpCode as ampResendSignUpCode,
  resetPassword as ampResetPassword,
  signIn as ampSignIn,
  signInWithRedirect as ampSignInWithRedirect,
  signOut as ampSignout,
  signUp as ampSignup,
  updateMFAPreference as ampUpdateMFAPreference,
  updatePassword as ampUpdatePassword,
  ConfirmSignUpOutput,
  fetchAuthSession,
  getCurrentUser,
  ResendSignUpCodeOutput,
  ResetPasswordOutput,
  SignInOutput,
  SignOutInput,
  SignUpOutput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  createContext,
  JSX,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppType } from '../../provider/provider.type';
import { selectConfig } from '../config';

export type DeviceAuth = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresTimestamp: number;
};

export type AuthenticationContextType = {
  isLoggedIn: boolean;
  jwt?: string;
  deviceAuth?: DeviceAuth;
  email?: string;
  app: 'web' | 'mobile' | 'ctv';
  signIn: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<SignInOutput>;
  confirmSignIn: (optCode: string) => Promise<void>;
  signUp: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<SignUpOutput>;
  confirmSignUp: ({
    email,
    confirmationCode,
  }: {
    email: string;
    confirmationCode: string;
  }) => Promise<ConfirmSignUpOutput>;
  signOut: (input?: SignOutInput) => Promise<void>;
  resendSignUpCode: (email: string) => Promise<ResendSignUpCodeOutput>;
  resetPassword: (email: string) => Promise<ResetPasswordOutput>;
  confirmResetPassword: ({
    email,
    confirmationCode,
    newPassword,
  }: {
    email: string;
    confirmationCode: string;
    newPassword: string;
  }) => Promise<void>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  deleteUser: () => Promise<void>;
  autoSignIn: () => Promise<SignInOutput>;
  deviceAuthGranted?: (jwt: DeviceAuth) => void;
  deviceAuthRejected?: () => void;
  signInWithVipps?: () => Promise<void>;
  checkUser: () => Promise<void>;
};

export const AuthenticationContext =
  createContext<AuthenticationContextType | null>(null);

export type AuthenticationProviderType = ({
  app,
  children,
}: {
  app: AppType;
  children: ReactNode;
}) => JSX.Element;

export const AuthenticationProvider: AuthenticationProviderType = ({
  app,
  children,
}) => {
  // fetch jwt from amplify
  const [jwt, setJWT] = useState<string>();
  const [email, setEmail] = useState<string>();
  const queryClient = useQueryClient();

  const REFRESH_TOKEN_BEFORE_EXPIRATION_MS = 120_000;
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback(
    (idToken: {
      payload: { exp?: number; email?: unknown };
      toString: () => string;
    }) => {
      const exp = idToken?.payload?.exp;
      if (!exp) return;
      const expMs = exp * 1000;
      const now = Date.now();
      const delay = Math.max(
        expMs - now - REFRESH_TOKEN_BEFORE_EXPIRATION_MS,
        5000,
      );

      clearRefreshTimeout();
      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const fresh = session.tokens?.idToken;
          if (!fresh) return;
          setJWT(fresh.toString());
          if (fresh.payload.email) setEmail(fresh.payload.email as string);
          scheduleTokenRefresh(
            fresh as unknown as {
              payload: { exp?: number; email?: unknown };
              toString: () => string;
            },
          );
        } catch (e) {
          console.error('Scheduled token refresh failed', e);
        }
      }, delay);
    },
    [REFRESH_TOKEN_BEFORE_EXPIRATION_MS, clearRefreshTimeout],
  );

  const checkUser = useCallback(async () => {
    const getSession = async () => {
      const session = await fetchAuthSession();
      let idToken = session.tokens?.idToken;
      const exp = idToken?.payload?.exp;
      if (
        exp &&
        exp * 1000 - Date.now() <= REFRESH_TOKEN_BEFORE_EXPIRATION_MS
      ) {
        const refreshed = await fetchAuthSession({ forceRefresh: true });
        idToken = refreshed.tokens?.idToken;
      }
      return idToken;
    };
    const idToken = await getSession();
    if (!idToken) return;
    setJWT(idToken.toString());

    if (!idToken.payload.email) return;
    setEmail(idToken.payload.email as string);
    scheduleTokenRefresh(
      idToken as unknown as {
        payload: { exp?: number; email?: unknown };
        toString: () => string;
      },
    );
  }, [REFRESH_TOKEN_BEFORE_EXPIRATION_MS, scheduleTokenRefresh]);

  useEffect(() => {
    if (app !== 'web') {
      Amplify.configure(selectConfig({ appType: app }));
      // Enable OAuth listener after Amplify is configured (required for Vipps redirect handling)
      import('aws-amplify/auth/enable-oauth-listener').catch((err) =>
        console.error('Failed to import enable-oauth-listener:', err),
      );
    }
    void checkUser();
    return () => {
      clearRefreshTimeout();
    };
  }, [app, checkUser, clearRefreshTimeout]);

  useEffect(() => {
    Hub.listen('auth', async (data) => {
      const { payload } = data;
      if (payload.event === 'signInWithRedirect') {
        try {
          const user = await getCurrentUser();
          if (user) {
            const session = await fetchAuthSession();
            setJWT(session.tokens?.idToken?.toString());
            if (session.tokens?.idToken?.payload.email) {
              setEmail(session.tokens.idToken.payload.email as string);
            }
            void checkUser();
          }
        } catch (error) {
          console.error('Error in signInWithRedirect handler:', error);
        }
      }
      if (payload.event === 'signedOut') {
        setJWT(undefined);
        setEmail(undefined);
        clearRefreshTimeout();
        void queryClient.invalidateQueries();
      } else if (payload.event === 'signedIn') {
        void checkUser();
      } else if (payload.event === 'tokenRefresh') {
        void checkUser();
      }
    });
  }, [queryClient, checkUser, clearRefreshTimeout]);

  // https://docs.amplify.aws/javascript/build-a-backend/auth/enable-sign-up/#sign-in
  type SignInParameters = {
    email: string;
    password: string;
  };

  const signIn = async ({ email, password }: SignInParameters) => {
    try {
      const res = await ampSignIn({ username: email, password });

      if (res.isSignedIn) {
        // Immediately update state for successful sign-in
        await checkUser();
        await authenticateUserInRestAPI();
      } else {
        // For other flows like MFA, just authenticate in REST API
        await authenticateUserInRestAPI();
      }

      return res;
    } catch (error) {
      console.error('Error while signing in:', error);
      throw error;
    }
  };

  const autoSignIn = () => {
    return ampAutoSignIn();
  };

  type SignUpParameters = {
    email: string;
    password: string;
  };

  const signUp = ({ email, password }: SignUpParameters) => {
    return ampSignup({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
        autoSignIn: true,
      },
    });
  };

  type SignUpConfirmParameters = {
    email: string;
    confirmationCode: string;
  };
  const confirmSignUp = ({
    email,
    confirmationCode,
  }: SignUpConfirmParameters) => {
    return ampConfirmSignup({ username: email, confirmationCode });
  };

  const signOut = async (input?: SignOutInput) => {
    // kill auth session
    await ampSignout(input);

    if (app === 'web') {
      // kill subprofile session cookie
      document.cookie =
        'subprofile=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // kill subprofile token session cookie
      document.cookie =
        'subprofile_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // redirect to homepage
      window.location.href = '/';
    }

    await queryClient.resetQueries();
    queryClient.clear();
  };

  const resendSignUpCode = async (email: string) => {
    return ampResendSignUpCode({ username: email });
  };

  const resetPassword = async (email: string) => {
    return ampResetPassword({ username: email });
  };

  type ConfirmResetPasswordParameters = {
    email: string;
    confirmationCode: string;
    newPassword: string;
  };
  const confirmResetPassword = async ({
    email,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordParameters) => {
    return ampConfirmResetPassword({
      username: email,
      confirmationCode,
      newPassword,
    });
  };

  const updatePassword = async (oldPassword: string, newPassword: string) => {
    return ampUpdatePassword({ oldPassword, newPassword });
  };

  const deleteUser = async () => {
    try {
      // TODO: delete VIA Profiles data first
      // https://developer.vimond.com/docs/gdpr
      // https://vimond-ng.atlassian.net/wiki/spaces/SVCS/pages/918716592/GDPR

      // delete user viewing history
      // https://developer.vimond.com/reference/deleteuserhistoryusingdelete

      // delete playlists
      // https://developer.vimond.com/reference/deleteusingdelete_1

      // Delete all subprofiles for logged in user
      // https://developer.vimond.com/reference/deletetop

      // delete device registation
      // https://developer.vimond.com/reference/deleteusingdelete-2

      await ampDeleteUser();
      await signOut();
    } catch (error) {
      console.error(error);
    }
  };

  const confirmSignIn = async (optCode: string) => {
    return ampConfirmSignIn({ challengeResponse: optCode }).then(async () => {
      await updateMFAPreference();
    });
  };

  const updateMFAPreference = async () => {
    return ampUpdateMFAPreference({ totp: 'PREFERRED' });
  };

  const signInWithVipps = useCallback(() => {
    return ampSignInWithRedirect({
      provider: {
        custom: 'vipps',
      },
    });
  }, []);

  return (
    <AuthenticationContext.Provider
      value={{
        isLoggedIn: !!jwt,
        jwt,
        email,
        app,
        signIn,
        confirmSignIn,
        signUp,
        confirmSignUp,
        signOut,
        resendSignUpCode,
        resetPassword,
        confirmResetPassword,
        updatePassword,
        deleteUser,
        autoSignIn,
        signInWithVipps,
        checkUser,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
